export const meetingFacilitationModule = {
  id: "tc-meeting-facilitation",
  title: "Expert Meeting Facilitation",
  description: "Lead productive meetings that drive decisions, maintain engagement, and respect everyone's time.",
  content: `
## What You'll Learn
By the end of this module, you will be able to:
- Design and facilitate meetings that consistently achieve their intended outcomes
- Apply group dynamics principles to encourage participation from all attendees
- Navigate conflict and disagreement constructively to reach consensus
- Implement decision-making frameworks that create clarity and accountability

## Prerequisites
- Experience participating in team meetings
- Basic understanding of your organization's meeting culture

## Overview
Poor meeting facilitation costs organizations millions annually in lost productivity. The average knowledge worker spends 37% of their time in meetings, yet only 25% report that meetings are generally productive. Master facilitators can transform meeting culture, driving better decisions, stronger relationships, and measurable business outcomes.

---

## Key Concepts

### The PREP Meeting Framework
Professional facilitators use the PREP framework: Purpose, Roles, Engagement, and Process. This structure ensures every meeting has clear intentions and achievable outcomes.

#### Why It Matters
Meetings that follow the PREP framework are 3x more likely to result in clear decisions and actionable next steps. Participants report 85% higher satisfaction when meetings have clear structure and purpose.

#### Best Practices
- Define specific, measurable outcomes before scheduling any meeting
- Assign clear roles: decision-maker, subject matter experts, stakeholders
- Choose engagement methods appropriate to your audience and objectives
- Document process decisions and follow-up commitments in real-time

### Inclusive Participation Techniques
Great facilitators ensure every voice is heard while maintaining momentum toward decisions. This requires intentional design and active management of group dynamics.

#### Industry Usage
Leading consulting firms and technology companies train all managers in inclusive facilitation because diverse input improves decision quality by 70% and increases buy-in for implementation.

---

## Practical Examples

### Example 1: The "Round Robin" Technique
Ensure balanced participation when discussing complex issues.

**Implementation:**
1. Pose a specific question or problem
2. Give everyone 2 minutes to write down their thoughts
3. Go around the room having each person share one key point
4. Build on ideas in subsequent rounds
5. Synthesize common themes and differences

### Example 2: Decision-Making Template
Use structured frameworks to move from discussion to commitment.

**The RAPID Framework:**
- **Recommend**: Who proposes the solution?
- **Agree**: Who must agree for implementation?
- **Perform**: Who will execute the decision?
- **Input**: Who provides expertise and information?
- **Decide**: Who makes the final call?

💡 **Pro Tip**: End every meeting with the "3 W's" - What was decided, Who is responsible, and When is the deadline. This simple practice increases follow-through rates by 90%.

---

## Industry Adoption

### Why Companies Choose This Approach
Organizations with strong meeting facilitation practices report 40% faster project completion, 50% better stakeholder alignment, and significantly higher employee engagement scores. Effective meetings are now recognized as a competitive advantage.

### Popular Tools and Libraries
- **Miro**: Visual collaboration platform for brainstorming and decision-making
- **Slido**: Real-time polling and Q&A for large group engagement
- **Calendly**: Scheduling tool with meeting preparation workflows

### Comparison with Alternatives
| Approach | Advantages | Use Cases |
|----------|------------|-----------|
| Structured Facilitation | Predictable outcomes, inclusive, efficient | Strategic planning, problem-solving, decision meetings |
| Open Discussion | Creative, flexible, relationship-building | Brainstorming, culture building, early-stage exploration |

---

## Key Takeaways

✅ **Essential Points**:
- Every meeting must have a clear purpose and defined success criteria
- Inclusive participation techniques improve both decision quality and team buy-in
- Structured processes move groups from discussion to decision efficiently
- Follow-up accountability systems ensure meetings translate into action

⚠️ **Common Pitfalls**:
- Allowing meetings to become status update sessions instead of collaborative work
- Failing to manage dominant personalities who prevent others from contributing

## Next Steps
Finally, you'll learn cross-team collaboration strategies that extend your communication impact across organizational boundaries and different working styles.

---
`,
  duration: "40 minutes",
  difficulty: "intermediate" as const,
  objectives: [
    "Master the PREP meeting framework",
    "Apply inclusive participation techniques",
    "Implement decision-making structures",
    "Create accountability systems"
  ],
  keyTopics: [
    "Meeting Design",
    "Group Dynamics",
    "Decision-Making Frameworks",
    "Conflict Resolution",
    "Follow-up Systems"
  ]
};